import classNames from "classnames";
import React, { useState } from "react";

type ImgProps = React.DetailedHTMLProps<
  React.ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
> & {
  className?: string;
  imageClassName?: string;
  src?: string;
  alt?: string;
  isAvatar?: boolean;
  enableShimmer?: boolean; // <-- NEW PROP
};

const shimmerStyles = `
  @keyframes shimmer {
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: calc(200px + 100%) 0;
    }
  }
  .shimmer-effect {
    background: linear-gradient(90deg, #f4f4f5 25%, #d4d4d8 50%, #f4f4f5 75%);
    background-size: 200px 100%;
    animation: shimmer 1.5s infinite;
    border-radius: inherit;
  }
`;

const Img: React.FC<ImgProps> = ({
  className = "",
  imageClassName = "",
  src = "defaultNoData.png",
  alt = "image",
  isAvatar = false,
  enableShimmer = true,
  style,
  ...restProps
}) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(enableShimmer);

  const resolvedSrc =
    src?.startsWith("/") || src?.startsWith("http") ? src : `/${src}`;

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (enableShimmer) {
      setTimeout(() => setIsLoading(false), 300); // optional shimmer delay
    }
    restProps.onLoad?.(e);
  };

  const handleImageError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  const shouldShowShimmer =
    enableShimmer && !isAvatar && isLoading && !hasError;

  if (hasError) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-200 ${className}`}
        style={style}
      >
        {/* fallback UI */}
      </div>
    );
  }

  return (
    <>
      {enableShimmer && <style>{shimmerStyles}</style>}
      <div className={`relative overflow-hidden ${className}`} style={style}>
        {shouldShowShimmer && (
          <div className="shimmer-effect absolute inset-0 z-10" />
        )}
        <img
          className={classNames(`w-full h-full object-contain transition-opacity duration-300`,shouldShowShimmer ? "opacity-0" : "opacity-100", imageClassName)}
          src={resolvedSrc}
          alt={alt}
          onLoad={handleImageLoad}
          onError={handleImageError}
          loading="lazy"
          {...restProps}
        />
      </div>
    </>
  );
};

export { Img };
